import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type {
  PasswordForgotten,
  Prompt,
  PromptDeposit,
  PromptResetPassword,
  PromptRestoreWallet,
  PromptSignupWithUsername,
  SetMode,
  SignUpMobileView,
  Sport,
} from '@sorare/wallet-shared';

import { Dialog } from '#src/components/Dialog.tsx';
import SignIn from '#src/components/SignIn.tsx';
import ChangePassword from '#src/forms/ChangePassword.tsx';
import DepositBaseBank from '#src/forms/DepositbaseBank/index.tsx';
import ForgotPassword from '#src/forms/ForgotPassword.tsx';
import GenerateKeys from '#src/forms/GenerateKeys.tsx';
import PrivateKey from '#src/forms/PrivateKey.tsx';
import ResetPassword from '#src/forms/ResetPassword.tsx';
import RestoreWallet from '#src/forms/RestoreWallet.tsx';
import { SignUp } from '#src/forms/SignUp/index.tsx';
import SolanaPrivateKey from '#src/forms/SolanaPrivateKey.tsx';
import useInfo from '#src/hooks/useInfo.ts';
import useRegisterPassword from '#src/hooks/useRegisterPassword.ts';

interface Context {
  openedForm?:
    | Prompt['request']['args']['type']
    | 'resetPassword'
    | 'restoreWallet';
  close: () => void;
}

const FormsContext = createContext<Context | null>(null);

type OpenedFormName = Context['openedForm'];

interface Props {
  children: ReactNode;
}

export const FormsProvider = ({ children }: Props) => {
  const { registerHandler, sendRequest } = useContext(MessagingContext)!;
  const { user } = useInfo();
  const [openedForm, setOpenedForm] = useState<OpenedFormName>(() =>
    user === undefined ? 'signIn' : undefined
  );
  const [backTo, setBackTo] = useState<OpenedFormName>(undefined);

  // Show sign-in when user becomes undefined (logs out)
  if (user === undefined && openedForm === undefined) {
    setOpenedForm('signIn');
  }
  const [mobileApp, setMobileApp] = useState<boolean>(false);
  const [depositId, setDepositId] = useState<string | undefined>(undefined);
  const [nickname, setNickname] = useState<string>();
  const [sport, setSport] = useState<Sport>();
  const [isAndroidApp, setIsAndroidApp] = useState<boolean>(false);
  const [resetPasswordData, setResetPasswordData] = useState<
    PromptResetPassword['request']['args'] | undefined
  >();
  const [restoreWalletData, setRestoreWalletData] = useState<
    PromptRestoreWallet['request']['args'] | undefined
  >();

  const setModeConnectionDialog = useCallback(
    async (mode: 'signin' | 'signup') => {
      await sendRequest<SetMode>('setMode', {
        mode,
      });
    },
    [sendRequest]
  );

  useRegisterPassword();

  useEffect(
    () =>
      registerHandler<Prompt>('prompt', async ({ type }) => {
        setOpenedForm(type);
        return {};
      }),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<PromptSignupWithUsername>(
        'promptSignupWithUsername',
        async ({
          username,
          isAndroidApp: isAndroidAppArg,
          sport: sportArg,
        }) => {
          setNickname(username);
          setSport(sportArg);
          setIsAndroidApp(isAndroidAppArg);
          setOpenedForm('signup');
          return {};
        }
      ),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<PromptDeposit>('promptDeposit', async ({ id }) => {
        setDepositId(id);
        setOpenedForm('deposit');
        return {};
      }),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<PasswordForgotten>(
        'passwordForgotten',
        async ({ isMobileApp }) => {
          setMobileApp(isMobileApp || false);
          setOpenedForm('passwordForgotten');
          return {};
        }
      ),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<SignUpMobileView>(
        'signUpMobileView',
        async ({ isAndroidApp: isAndroidAppArg }) => {
          setIsAndroidApp(isAndroidAppArg);
          setOpenedForm('signUpMobileView');
          return {};
        }
      ),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<PromptResetPassword>(
        'promptResetPassword',
        async args => {
          setOpenedForm('resetPassword');
          setResetPasswordData(args);
          return {};
        }
      ),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<PromptRestoreWallet>(
        'promptRestoreWallet',
        async args => {
          setOpenedForm('restoreWallet');
          setRestoreWalletData(args);
          return {};
        }
      ),
    [registerHandler]
  );

  const onSuccess = () => {
    setBackTo(undefined);
    setOpenedForm(undefined);
  };
  const onForgotPassword = () => {
    setBackTo(openedForm);
    setOpenedForm('passwordForgotten');
  };

  const dialogParams = useMemo(() => {
    switch (openedForm) {
      case 'signUpMobileView':
        return { className: `scroll-y-overlay ${openedForm}` };
      case 'signup':
        return { className: `V2WalletForm scroll-y-overlay ${openedForm}` };
      case 'signIn':
        return { className: `V2WalletForm scroll-y-overlay ${openedForm}` };
      case 'passwordForgotten':
        return { className: `V2WalletForm ${backTo ? 'wallet-drawer' : ''}` };
      case 'generateKeys':
      case 'changePassword':
      case 'deposit':
      case 'resetPassword':
      case 'restoreWallet':
      case 'privateKeyExport':
      case 'solanaPrivateKeyExport':
        return { className: `V2WalletForm wallet-drawer` };
      default:
        return {};
    }
  }, [backTo, openedForm]);

  return (
    <FormsContext value={{ openedForm, close: () => setOpenedForm(undefined) }}>
      {openedForm ? (
        <Dialog {...dialogParams}>
          {openedForm === 'generateKeys' && (
            <GenerateKeys onSuccess={onSuccess} />
          )}
          {openedForm === 'passwordForgotten' && (
            <ForgotPassword
              onSuccess={onSuccess}
              onBack={() => setOpenedForm(backTo)}
              mode={backTo ? 'drawer' : 'modal'}
              isMobileApp={mobileApp}
            />
          )}
          {openedForm === 'changePassword' && (
            <ChangePassword
              onSuccess={onSuccess}
              onForgotPassword={onForgotPassword}
            />
          )}
          {openedForm === 'deposit' && (
            <DepositBaseBank depositId={depositId} onSuccess={onSuccess} />
          )}
          {openedForm === 'resetPassword' && !!resetPasswordData && (
            <ResetPassword {...resetPasswordData} onSuccess={onSuccess} />
          )}
          {openedForm === 'restoreWallet' && !!restoreWalletData && (
            <RestoreWallet {...restoreWalletData} onSuccess={onSuccess} />
          )}
          {openedForm === 'privateKeyExport' && (
            <PrivateKey onSuccess={onSuccess} />
          )}
          {openedForm === 'solanaPrivateKeyExport' && (
            <SolanaPrivateKey onSuccess={onSuccess} />
          )}
          {(openedForm === 'signup' || openedForm === 'signUpMobileView') && (
            <SignUp
              nicknameReceived={nickname!}
              sportSelected={sport!}
              onSuccess={onSuccess}
              isAndroidApp={isAndroidApp}
            />
          )}
          {openedForm === 'signIn' && (
            <SignIn
              onSuccess={onSuccess}
              onForgotPassword={() => {
                setBackTo(undefined);
                setOpenedForm('passwordForgotten');
              }}
              switchToSignUp={() => {
                setModeConnectionDialog('signup');
              }}
            />
          )}
        </Dialog>
      ) : (
        children
      )}
    </FormsContext>
  );
};
